import { useState, useEffect } from 'react'
import { Receipt, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Image } from 'lucide-react'
import { format } from 'date-fns'
import { expenseApi, EXPENSE_CATEGORY_LABELS, EXPENSE_STATUS_LABELS, type Expense, type ExpenseCategory, type ExpenseStatus } from '../../api/expenses'
import { driverApi, type Driver } from '../../api/drivers'
import { Button } from '@/components/ui/button'

const STATUS_COLORS: Record<ExpenseStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

const STATUS_ICONS: Record<ExpenseStatus, React.ReactNode> = {
  pending: <Clock className="h-3 w-3" />,
  approved: <CheckCircle className="h-3 w-3" />,
  rejected: <XCircle className="h-3 w-3" />,
}

const CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  fuel: 'bg-blue-100 text-blue-700',
  maintenance: 'bg-purple-100 text-purple-700',
  parking: 'bg-gray-100 text-gray-700',
  toll: 'bg-orange-100 text-orange-700',
  other: 'bg-slate-100 text-slate-700',
}

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const limit = 15
  const [loading, setLoading] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | ''>('')
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | ''>('')
  const [driverFilter, setDriverFilter] = useState<string>('')
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const [reviewNote, setReviewNote] = useState<{ id: string; note: string } | null>(null)

  useEffect(() => {
    driverApi.getDrivers().then(res => setDrivers(res.items)).catch(() => {})
  }, [])

  const fetchExpenses = async () => {
    setLoading(true)
    try {
      const data = await expenseApi.getAllExpenses({
        page,
        limit,
        driver_id: driverFilter || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
      })
      setExpenses(data.items)
      setTotal(data.total)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchExpenses()
  }, [page, categoryFilter, statusFilter, driverFilter])

  const handleUpdateStatus = async (id: string, status: ExpenseStatus, note?: string) => {
    try {
      await expenseApi.updateExpense(id, { status, reviewer_note: note })
      setReviewNote(null)
      fetchExpenses()
    } catch (err: any) {
      alert(err?.response?.data?.detail || '更新失敗')
    }
  }

  const totalAmount = expenses.reduce((sum, e) => sum + Number(e.amount), 0)

  return (
    <div className="space-y-6 p-1.5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Receipt className="h-6 w-6 text-orange-500" />
            司機支出管理
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">檢視與審核所有司機支出記錄</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: '全部', status: '', count: null },
          { label: '待審核', status: 'pending' as ExpenseStatus, count: null },
          { label: '已通過', status: 'approved' as ExpenseStatus, count: null },
          { label: '已駁回', status: 'rejected' as ExpenseStatus, count: null },
        ].map(({ label, status }) => (
          <button
            key={label}
            onClick={() => { setStatusFilter(status as any); setPage(1) }}
            className={`rounded-xl border px-4 py-3 text-left transition-colors cursor-pointer ${
              statusFilter === status
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={driverFilter}
          onChange={(e) => { setDriverFilter(e.target.value); setPage(1) }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:ring-1 focus:ring-blue-500"
        >
          <option value="">所有司機</option>
          {drivers.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value as any); setPage(1) }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:ring-1 focus:ring-blue-500"
        >
          <option value="">所有類別</option>
          {Object.entries(EXPENSE_CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1) }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:ring-1 focus:ring-blue-500"
        >
          <option value="">所有狀態</option>
          {Object.entries(EXPENSE_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {(driverFilter || categoryFilter || statusFilter) && (
          <Button variant="outline" onClick={() => { setDriverFilter(''); setCategoryFilter(''); setStatusFilter(''); setPage(1) }} className="text-sm">
            清除篩選
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        {/* Mobile */}
        <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
          {loading ? (
            <div className="p-8 text-center text-gray-400">載入中...</div>
          ) : expenses.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm text-gray-400">無支出記錄</p>
            </div>
          ) : expenses.map((expense) => (
            <div key={expense.id} className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">{expense.driver_name}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[expense.category]}`}>
                      {EXPENSE_CATEGORY_LABELS[expense.category]}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[expense.status]}`}>
                      {STATUS_ICONS[expense.status]}{EXPENSE_STATUS_LABELS[expense.status]}
                    </span>
                  </div>
                  <div className="text-base font-bold text-orange-600 mt-0.5">NT$ {Number(expense.amount).toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{format(new Date(expense.expense_date), 'yyyy/MM/dd')}{expense.description && ` · ${expense.description}`}</div>
                </div>
                {expense.receipt_image_path && (
                  <button onClick={() => setLightboxSrc(expense.receipt_image_path!)} className="cursor-pointer">
                    <img src={expense.receipt_image_path} alt="receipt" className="h-14 w-14 rounded-lg object-cover border hover:opacity-80" />
                  </button>
                )}
              </div>
              {expense.status === 'pending' && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white text-xs flex-1" onClick={() => setReviewNote({ id: expense.id, note: '' })}>
                    <CheckCircle className="h-3 w-3 mr-1" /> 審核
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-5 py-3 font-medium">司機</th>
                <th className="px-5 py-3 font-medium">類別</th>
                <th className="px-5 py-3 font-medium">金額</th>
                <th className="px-5 py-3 font-medium">日期</th>
                <th className="px-5 py-3 font-medium">備注</th>
                <th className="px-5 py-3 font-medium">發票</th>
                <th className="px-5 py-3 font-medium">狀態</th>
                <th className="px-5 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-gray-400">載入中...</td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-gray-400">無符合條件的支出</td></tr>
              ) : expenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{expense.driver_name}</div>
                    {expense.tenant_name && <div className="text-xs text-gray-400">{expense.tenant_name}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CATEGORY_COLORS[expense.category]}`}>
                      {EXPENSE_CATEGORY_LABELS[expense.category]}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-bold text-orange-600 dark:text-orange-400">
                    NT$ {Number(expense.amount).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {format(new Date(expense.expense_date), 'yyyy/MM/dd')}
                  </td>
                  <td className="px-5 py-3 text-gray-500 max-w-[150px] truncate">{expense.description || '-'}</td>
                  <td className="px-5 py-3">
                    {expense.receipt_image_path ? (
                      <button onClick={() => setLightboxSrc(expense.receipt_image_path!)} className="cursor-pointer group">
                        <img src={expense.receipt_image_path} alt="receipt" className="h-10 w-10 rounded-lg object-cover border hover:opacity-80 transition-opacity" />
                      </button>
                    ) : (
                      <span className="text-gray-300"><Image className="h-5 w-5" /></span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[expense.status]}`}>
                      {STATUS_ICONS[expense.status]}
                      {EXPENSE_STATUS_LABELS[expense.status]}
                    </span>
                    {expense.reviewer_note && (
                      <div className="text-xs text-gray-400 mt-0.5 max-w-[120px] truncate" title={expense.reviewer_note}>
                        {expense.reviewer_note}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {expense.status === 'pending' && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleUpdateStatus(expense.id, 'approved')}
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 cursor-pointer transition-colors"
                          title="通過"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setReviewNote({ id: expense.id, note: '' })}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                          title="駁回並填備注"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-5 py-3">
          <div className="text-sm text-gray-500">
            共 <span className="font-medium">{total}</span> 筆
            {expenses.length > 0 && (
              <span className="ml-3 text-orange-600 font-semibold">
                本頁合計：NT$ {totalAmount.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="flex items-center px-3 text-sm text-gray-600 dark:text-gray-300">第 {page} 頁</span>
            <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {reviewNote && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="font-bold text-gray-900 dark:text-white">審核備注（選填）</h3>
            <textarea
              className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none resize-none"
              rows={3}
              placeholder="輸入駁回原因或備注..."
              value={reviewNote.note}
              onChange={(e) => setReviewNote({ ...reviewNote, note: e.target.value })}
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setReviewNote(null)}>取消</Button>
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => handleUpdateStatus(reviewNote.id, 'approved', reviewNote.note)}
              >
                通過
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => handleUpdateStatus(reviewNote.id, 'rejected', reviewNote.note)}
              >
                駁回
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxSrc(null)}
        >
          <img src={lightboxSrc} alt="receipt" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}
    </div>
  )
}
